'use client'

import { useState } from 'react'
import { Flex, Group, Skeleton, Stack, Text, Loader } from '@mantine/core'
import { IconHelpCircle } from '@tabler/icons-react'
import { useLabSkuPricing } from '@/hooks/useLabSkuPricing'
import { AboutLabPricing } from './Labs/AboutLabPricing'
import DrawerOrModalContent from '@/components/shared/DrawerOrModalContent'
import { useUserContext } from '@/contexts/UserContext'
import { useInsuranceContext } from '@/contexts/InsuranceContext'
import { useRouter, usePathname } from 'next/navigation'
import { setInsuranceReturnPath } from '@/lib/redirectFromInsuranceFlow'
import { formatPrice } from '@/lib/utilities'
import { useBookVisitDetailHref } from '@/hooks/useBookVisitHref'
import { ESTIMATED_INSURANCE_COPAY } from '@/types/enums/lab-pricing-constants'
import { trackEvent, SegmentEvents } from '@nile/mantine-theme/analytics'
interface LabPanelPricingProps {
    skuId: string
    wellnessSku?: boolean
    fullWidth?: boolean
    skuPageVariant?: 'refresh' | 'legacy'
}

export function LabPanelPricing({
    skuId,
    wellnessSku = false,
    fullWidth = false,
    skuPageVariant,
}: LabPanelPricingProps) {
    const { labSku, cashPrice, insurancePrice, isInsuranceOutOfNetwork, isClinicalFeeEnabled, clinicalFee, isLoading } =
        useLabSkuPricing(skuId)
    const [drawerOpened, setDrawerOpened] = useState(false)
    const { user } = useUserContext()
    const isGuest = user === null
    const bookVisitDetailHref = useBookVisitDetailHref()
    const { userInsurance } = useInsuranceContext()
    const hasInsurance = !!userInsurance
    const router = useRouter()
    const pathname = usePathname()
    const trackPricingClick = (buttonType: string) => {
        if (!skuPageVariant) return
        trackEvent(SegmentEvents.SKU_PAGE_CTA_CLICKED, {
            sku_id: skuId,
            button_type: buttonType,
            variant: skuPageVariant,
        })
    }

    if (isLoading) {
        return (
            <Stack gap={15}>
                <Stack gap="sm">
                    <Skeleton height={24} width={150} />
                    <Skeleton height={48} width={200} />
                    <Skeleton height={20} width={250} />
                    <Skeleton height={20} width={150} />
                </Stack>
            </Stack>
        )
    }

    const formattedCashPrice = `$${(cashPrice ?? 0).toFixed(2)}`
    let formattedInsurancePrice: string | null
    let isInsuranceEstimate = false
    let isInsuranceOrderable = true

    if (insurancePrice !== null && Number.isFinite(insurancePrice)) {
        formattedInsurancePrice = `$${insurancePrice.toFixed(2)}`
    } else if (labSku?.insurance_orderable === true) {
        formattedInsurancePrice = `$${ESTIMATED_INSURANCE_COPAY}`
        isInsuranceEstimate = true
    } else {
        formattedInsurancePrice = null
        isInsuranceOrderable = false
    }

    const isCashPriceValid = cashPrice !== null && Number.isFinite(cashPrice)
    let minimumPriceType = 'cash'
    // A typical copay is not a fair stand-in for an out-of-network lab, so cash leads whenever it is available.
    const leadsWithCashOverCopayEstimate = isInsuranceOutOfNetwork && isInsuranceEstimate && isCashPriceValid

    if (formattedInsurancePrice !== null && !leadsWithCashOverCopayEstimate) {
        const effectiveInsurancePrice =
            insurancePrice !== null && Number.isFinite(insurancePrice) ? insurancePrice : ESTIMATED_INSURANCE_COPAY
        const effectiveCashPrice = isCashPriceValid ? (cashPrice as number) : Infinity

        if (effectiveInsurancePrice < effectiveCashPrice) {
            minimumPriceType = 'insurance'
        }
    }

    return (
        <Stack gap={15}>
            <Stack
                gap="xs"
                styles={{
                    root: {
                        width: '100%',
                        ...(fullWidth ? {} : { maxWidth: 450 }),
                    },
                }}
            >
                {renderPricing()}
                {renderPriceDescription()}
            </Stack>

            {renderAboutOurLabDrawer()}
        </Stack>
    )

    function renderClinicalReviewInfo() {
        if (!isClinicalFeeEnabled) return null

        return (
            <Group gap={5}>
                <Text c="gray.6" fz={16} fw={500} lh={'21px'}>
                    ${formatPrice(clinicalFee, 0)} clinical review added at checkout
                </Text>
                <IconHelpCircle
                    size={18}
                    color="blue"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setDrawerOpened(true)}
                />
            </Group>
        )
    }

    function renderPricing() {
        if (isLoading) {
            return <Loader size={32} color="gray" />
        }

        return (
            <Text c="black" fz={30} fw={400} lh={'125%'} lts={'-0.25px'} span>
                {minimumPriceType === 'cash' ? formattedCashPrice : formattedInsurancePrice}
                {minimumPriceType === 'insurance' && isInsuranceEstimate && '*'}
                {wellnessSku && (
                    <Text span ml="xs" fz={30} c="gray.5" td="line-through" fs="italic">
                        $250
                    </Text>
                )}
            </Text>
        )
    }

    function renderPriceDescription() {
        return minimumPriceType === 'cash' ? (
            <Flex direction="column" gap={8}>
                {formattedInsurancePrice &&
                    isInsuranceOrderable &&
                    (isInsuranceEstimate && (isInsuranceOutOfNetwork || (!isGuest && hasInsurance)) ? (
                        <>
                            <Text c="gray.6" fz={16} fw={500} lh={'21px'}>
                                Without insurance.
                            </Text>
                            <Text c="gray.6" fz={16} fw={500} lh={'21px'}>
                                We can&apos;t estimate your insurance cost at this time. The lab will bill your insurer
                                directly and bill you any out-of-pocket cost.
                            </Text>
                        </>
                    ) : (
                        <Text c="gray.6" fz={16} fw={500} lh={'21px'}>
                            {isInsuranceEstimate
                                ? `Without insurance, or ${formattedInsurancePrice} typical insurance copay.`
                                : `Without insurance, or ${formattedInsurancePrice} with insurance.`}
                            {(isGuest || !hasInsurance) && (
                                <>
                                    &nbsp;
                                    <Text
                                        span
                                        c="blue"
                                        fz={16}
                                        fw={500}
                                        lh={'21px'}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => {
                                            trackPricingClick('check_insurance_price')
                                            setInsuranceReturnPath(pathname)
                                            router.push('/insurance')
                                        }}
                                    >
                                        Check insurance price&nbsp;→
                                    </Text>
                                </>
                            )}
                        </Text>
                    ))}
                {!formattedInsurancePrice && (
                    <Text c="gray.6" fz={16} fw={500} lh={'21px'}>
                        {isInsuranceOrderable
                            ? `Without insurance, or $${ESTIMATED_INSURANCE_COPAY} typical insurance copay.`
                            : `Without insurance. Not self-bookable with insurance.`}
                        {isInsuranceOrderable && (isGuest || !hasInsurance) && (
                            <>
                                &nbsp;
                                <Text
                                    span
                                    c="blue"
                                    fz={16}
                                    fw={500}
                                    lh={'21px'}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => {
                                        trackPricingClick('check_insurance_price')
                                        setInsuranceReturnPath(pathname)
                                        router.push('/insurance')
                                    }}
                                >
                                    Check insurance price&nbsp;→
                                </Text>
                            </>
                        )}
                        {!isInsuranceOrderable && (
                            <>
                                &nbsp;
                                <Text
                                    span
                                    c="blue"
                                    fz={16}
                                    fw={500}
                                    lh={'21px'}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => {
                                        trackPricingClick('book_visit_insurance')
                                        router.push(bookVisitDetailHref)
                                    }}
                                >
                                    Book a visit to use insurance&nbsp;&nbsp;→
                                </Text>
                            </>
                        )}
                    </Text>
                )}

                {renderClinicalReviewInfo()}
            </Flex>
        ) : (
            <Flex direction="column" gap={8}>
                {isInsuranceEstimate && !isGuest && hasInsurance ? (
                    <Text c="gray.6" fz={16} fw={500} lh={'21px'}>
                        *Typical insurance copay{isCashPriceValid ? `, or ${formattedCashPrice} without insurance` : ''}
                        . The lab will bill your insurer directly and bill you any out-of-pocket cost.
                    </Text>
                ) : (
                    <Text c="gray.6" fz={16} fw={500} lh={'21px'}>
                        {isInsuranceEstimate
                            ? `*Typical insurance copay${isCashPriceValid ? `, or ${formattedCashPrice} without insurance` : ''}.`
                            : `With insurance${isCashPriceValid ? `, or ${formattedCashPrice} without insurance` : ''}.`}
                        {isGuest && (
                            <>
                                &nbsp;
                                <Text
                                    span
                                    c="blue"
                                    fz={16}
                                    fw={500}
                                    lh={'21px'}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => {
                                        trackPricingClick('check_insurance_price')
                                        setInsuranceReturnPath(pathname)
                                        router.push('/insurance')
                                    }}
                                >
                                    Check insurance price&nbsp;→
                                </Text>
                            </>
                        )}
                    </Text>
                )}
                {renderClinicalReviewInfo()}
            </Flex>
        )
    }

    function renderAboutOurLabDrawer() {
        if (!isClinicalFeeEnabled) return null

        return (
            <DrawerOrModalContent
                content={<AboutLabPricing onClose={() => setDrawerOpened(false)} clinicalFee={clinicalFee} />}
                showContent={drawerOpened}
                setShowContent={setDrawerOpened}
                onClose={() => setDrawerOpened(false)}
                withCloseButton={false}
                size="65%"
            />
        )
    }
}
